import { motion } from 'framer-motion';
import { Star, GitBranch, CircleAlert, Lock } from 'lucide-react';
import { Card } from '../ui/Card';
import { formatRelativeTime } from '../../utils/format';
import type { GitHubRepo } from '../../types';

const LANGUAGE_COLORS: Record<string, string> = {
  TypeScript: '#3178c6',
  JavaScript: '#f1e05a',
  Python: '#3572A5',
  Go: '#00ADD8',
  Ruby: '#701516',
  PHP: '#4F5D95',
  Shell: '#89e051',
  HCL: '#844FBA',
};

export function RepoCard({ repo, index = 0 }: { repo: GitHubRepo; index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.04 }}
    >
      <Card className="h-full p-5">
        <a
          href={repo.htmlUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1.5 font-semibold text-slate-900 hover:text-pulse-600 dark:text-white dark:hover:text-pulse-400"
        >
          {repo.isPrivate && <Lock size={13} className="shrink-0 text-slate-400" />}
          <span className="truncate">{repo.name}</span>
        </a>
        {repo.description && (
          <p className="mt-1.5 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">{repo.description}</p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-400">
          {repo.language && (
            <span className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: LANGUAGE_COLORS[repo.language] ?? '#94a3b8' }}
              />
              {repo.language}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Star size={13} /> {repo.stargazersCount}
          </span>
          <span className="flex items-center gap-1">
            <CircleAlert size={13} /> {repo.openIssuesCount}
          </span>
          <span className="flex items-center gap-1">
            <GitBranch size={13} /> {repo.defaultBranch}
          </span>
        </div>

        <p className="mt-3 text-[11px] text-slate-400">Pushed {formatRelativeTime(repo.pushedAt)}</p>
      </Card>
    </motion.div>
  );
}
